// Pure, reusable field registry for the bulk "AI Enrich (full)" tool.
//
// The single-ref AI enrich modal in ReferenceEditor.tsx maps each suggestion key
// to a { label, currentDisplay, apply } handler — but those `apply` functions are
// closures over component state setters, so they can't be reused for a headless
// bulk write. This module re-expresses the SAME field set as PURE functions:
//   - getExisting(ref)  → the ref's current value as a display string ('' = empty)
//   - buildPatch(value) → a Firestore dotted-path patch (null if value unusable)
//
// The patch keys mirror exactly what ReferenceEditor.handleSave() persists
// (including the legacy mirror fields like case.diameter/diameterMm/sizeMm and the
// top-level `dial` mirror), and the read paths mirror ReferenceEditor's state
// initializers — so a bulk write is indistinguishable from a manual editor save.
//
// Dotted-path keys (e.g. 'case.material') let updateDoc() merge a single nested
// field without clobbering its siblings.

import type { WatchReference } from '../types';
import { formatDialMarkerValues } from '../utils/dialMarkerFields';
import { formatMultiLookupValues } from '../utils/multiLookupValues';
import { buildEnrichReferencePayload } from './enrichReferencePayload';
import type { SiblingReference } from './notableVariantCandidates';

export type ConfidenceLevel = 'high' | 'medium' | 'low';
export type EnrichThreshold = 'high' | 'highMedium';
export type EnrichWriteMode = 'fill' | 'overwrite';

export interface EnrichFieldSpec {
  /** Suggestion key returned by the enrichReference callable. */
  key: string;
  /** Human label (mirrors ReferenceEditor's aiFieldHandlers labels). */
  label: string;
  /** Current value on the ref as a display string. '' means "no existing value". */
  getExisting: (ref: WatchReference) => string;
  /**
   * True for a true/false field. Booleans tick by their own rule (see
   * {@link booleanPreTicks}) because a stored `false` is usually "nobody has
   * said yet", not an answer, so the generic "differs → leave it" rule made the
   * reviewer hand-tick every genuine discovery.
   */
  isBoolean?: true;
  /** Firestore dotted-path patch for an accepted suggestion, or null if the
   *  value can't be coerced into the field's stored shape. */
  buildPatch: (value: unknown) => Record<string, unknown> | null;
  /** Optional per-field override of the default-checked logic. When present it
   *  wholly replaces the threshold + write-mode rules in `defaultChecked`. Used
   *  for description, which we always want pre-checked on high confidence
   *  (high-churn AI field admins routinely refresh), regardless of write-mode or
   *  an existing value. */
  defaultChecked?: (opts: {
    confidence: string | undefined;
    hasExisting: boolean;
    threshold: EnrichThreshold;
    writeMode: EnrichWriteMode;
  }) => boolean;
}

// ── coercion helpers ────────────────────────────────────────────────────────

function toStr(v: unknown): string | null {
  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' ? null : t;
  }
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toInt(v: unknown): number | null {
  const n = toNumber(v);
  return n === null ? null : Math.round(n);
}

function toBool(v: unknown): boolean | null {
  return typeof v === 'boolean' ? v : null;
}

/** Multi-value → comma-joined string (how dial/hands colours & occasion persist). */
function toJoined(v: unknown): string | null {
  if (Array.isArray(v)) {
    const parts = v.map((x) => String(x).trim()).filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }
  return toStr(v);
}

/** Multi-value → string[] (how `functions` persists). */
function toArray(v: unknown): string[] | null {
  if (Array.isArray(v)) {
    const parts = v.map((x) => String(x).trim()).filter(Boolean);
    return parts.length ? parts : null;
  }
  if (typeof v === 'string') {
    const parts = v
      .split(/[,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.length ? parts : null;
  }
  return null;
}

// ── existing-value readers ──────────────────────────────────────────────────
// Mirror ReferenceEditor's useState initializers so "has existing value"
// detection matches what the editor would show as the current value.

/** Loosely-typed nested access for compat/transitional fields not on the type. */
function loose(ref: WatchReference): Record<string, any> {
  return ref as unknown as Record<string, any>;
}

function dispStr(v: unknown): string {
  if (v === undefined || v === null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  return '';
}

/** Booleans: '' when truly unset (undefined), else 'Yes'/'No'. A stored `false`
 *  counts as an existing value (so "fill empty only" won't overwrite it). */
function dispBool(v: unknown): string {
  if (v === undefined || v === null) return '';
  return v ? 'Yes' : 'No';
}

function dispJoined(v: unknown): string {
  if (Array.isArray(v)) {
    return v
      .map((x) => (typeof x === 'string' ? x : (x?.displayValue ?? '')))
      .filter(Boolean)
      .join(', ');
  }
  return dispStr(v);
}

// Convenience builders for the common single-field shapes.
const strField = (
  key: string,
  label: string,
  get: (r: WatchReference) => string,
  patchKey: string,
): EnrichFieldSpec => ({
  key,
  label,
  getExisting: get,
  buildPatch: (v) => {
    const s = toStr(v);
    return s === null ? null : { [patchKey]: s };
  },
});

const boolField = (
  key: string,
  label: string,
  get: (r: WatchReference) => string,
  patchKey: string,
): EnrichFieldSpec => ({
  key,
  label,
  isBoolean: true,
  getExisting: get,
  buildPatch: (v) => {
    const b = toBool(v);
    return b === null ? null : { [patchKey]: b };
  },
});

const intField = (
  key: string,
  label: string,
  get: (r: WatchReference) => string,
  patchKey: string,
): EnrichFieldSpec => ({
  key,
  label,
  getExisting: get,
  buildPatch: (v) => {
    const n = toInt(v);
    return n === null ? null : { [patchKey]: n };
  },
});

const numField = (
  key: string,
  label: string,
  get: (r: WatchReference) => string,
  patchKey: string,
): EnrichFieldSpec => ({
  key,
  label,
  getExisting: get,
  buildPatch: (v) => {
    const n = toNumber(v);
    return n === null ? null : { [patchKey]: n };
  },
});

// ── the registry ────────────────────────────────────────────────────────────
// Order here is the column order in the review grid. Note: calibre/powerReserve
// always map to the analog fields (calibre, movement.powerReserveHours). The
// editor remaps these to electronics.* for showModule watch types, but that
// depends on per-ref lookup state we don't resolve in bulk — the elecModule /
// elecBatteryLife keys below cover the module path directly.

const SPEC_LIST: EnrichFieldSpec[] = [
  // `model` is a REQUIRED field, so it's always populated and "fill empty" never
  // pre-checks it. But Shopify imports routinely pollute it with a colourway
  // (Ballast: model = "WALNUT" instead of "Perisher"). The AI proposes the correct
  // model when it can read the product page — this spec lets that suggestion appear
  // as a column and be applied when the user checks it (overwrite mode, or a manual
  // per-cell check — same as the single-ref editor's overwrite toggle).
  strField('model', 'Model', (r) => dispStr(r.model), 'model'),
  strField('calibre', 'Calibre', (r) => dispStr(r.calibre), 'calibre'),
  strField('movementType', 'Movement type', (r) => dispStr(r.movementType), 'movementType'),
  strField('caseMaterial', 'Case material', (r) => dispStr(r.case?.material), 'case.material'),
  {
    // Crystal — multi-select (lookup_crystal_materials), stored as an ARRAY
    // (not the comma-joined convention handsColor/indexColor use below): the
    // material and its features sit in one list. formatMultiLookupValues reads
    // the pre-migration scalar too, so a ref that has not been through
    // migrateCrownTypeCrystal still reports its existing value and is left
    // alone by a fill-empty run.
    key: 'crystal',
    label: 'Crystal',
    getExisting: (r) => formatMultiLookupValues(loose(r).crystal),
    buildPatch: (v) => {
      const a = toArray(v);
      return a === null ? null : { crystal: a };
    },
  },
  {
    // Crown type — same shape (lookup_crown_types): attachment + sealing
    // system + shape in one list.
    key: 'crownType',
    label: 'Crown type',
    getExisting: (r) => formatMultiLookupValues(loose(r).crownType),
    buildPatch: (v) => {
      const a = toArray(v);
      return a === null ? null : { crownType: a };
    },
  },
  strField('caseFinish', 'Case finish', (r) => dispStr(r.case?.finish), 'case.finish'),
  {
    key: 'waterRes',
    label: 'Water resistance (m)',
    getExisting: (r) =>
      dispStr(r.case?.waterResistanceM) || dispStr(loose(r).case?.waterResistance),
    buildPatch: (v) => {
      const n = toNumber(v);
      if (n === null) {
        const s = toStr(v);
        return s === null ? null : { 'case.waterResistance': s };
      }
      return { 'case.waterResistanceM': n, 'case.waterResistance': String(n) };
    },
  },
  {
    key: 'caseSize',
    label: 'Case size (mm)',
    getExisting: (r) =>
      dispStr(r.case?.diameterMm) ||
      dispStr(loose(r).case?.sizeMm) ||
      dispStr(loose(r).case?.diameter),
    buildPatch: (v) => {
      const n = toNumber(v);
      return n === null
        ? null
        : { 'case.diameter': n, 'case.diameterMm': n, 'case.sizeMm': n };
    },
  },
  {
    key: 'caseHeight',
    label: 'Case height (mm)',
    getExisting: (r) => dispStr(r.case?.heightMm) || dispStr(loose(r).case?.thickness),
    buildPatch: (v) => {
      const n = toNumber(v);
      return n === null ? null : { 'case.heightMm': n, 'case.thickness': n };
    },
  },
  {
    ...strField('description', 'Description', (r) => dispStr(r.description), 'description'),
    // Description is a high-churn AI-generated field admins almost always want to
    // refresh when the AI is confident. Pre-check unconditionally on high
    // confidence (ignoring write-mode + existing value); medium/low fall back to
    // the normal threshold + write-mode rule.
    defaultChecked: (opts) =>
      (opts.confidence ?? '').toLowerCase() === 'high' ? true : defaultChecked(opts),
  },
  strField('bezelType', 'Bezel type', (r) => dispStr(loose(r).bezelType), 'bezelType'),
  strField('strapMaterial', 'Strap material', (r) => dispStr(r.strap?.material), 'strap.material'),
  {
    key: 'dialColor',
    label: 'Dial color',
    getExisting: (r) => dispJoined(r.dialAndHands?.color) || dispStr(loose(r).dial),
    buildPatch: (v) => {
      const j = toJoined(v);
      return j === null ? null : { 'dialAndHands.color': j, dial: j };
    },
  },
  strField('dialStyle', 'Dial style', (r) => dispStr(loose(r).dialStyle), 'dialStyle'),
  boolField(
    'isChronograph',
    'Is chronograph',
    (r) => dispBool(r.movement?.isChronograph),
    'movement.isChronograph',
  ),
  boolField('isGMT', 'Is GMT', (r) => dispBool(r.movement?.isGMT), 'movement.isGMT'),
  strField('collection', 'Collection', (r) => dispStr(r.collection), 'collection'),
  intField('jewels', 'Jewels', (r) => dispStr(r.movement?.jewels), 'movement.jewels'),
  intField(
    'powerReserve',
    'Power reserve (h)',
    (r) => dispStr(r.movement?.powerReserveHours),
    'movement.powerReserveHours',
  ),
  strField('caseShape', 'Case shape', (r) => dispStr(r.case?.shape), 'case.shape'),
  {
    key: 'caseBack',
    label: 'Case back',
    getExisting: (r) => dispStr(loose(r).case?.caseBack) || dispStr(loose(r).case?.caseback),
    buildPatch: (v) => {
      const s = toStr(v);
      return s === null ? null : { 'case.caseBack': s, 'case.caseback': s };
    },
  },
  strField('caseCoating', 'Case coating', (r) => dispStr(loose(r).case?.coating), 'case.coating'),
  numField('caseWeight', 'Case weight (g)', (r) => dispStr(loose(r).case?.weightG), 'case.weightG'),
  {
    key: 'lugWidth',
    label: 'Lug width (mm)',
    getExisting: (r) => dispStr(loose(r).case?.lugWidthMm) || dispStr(loose(r).case?.lugWidth),
    buildPatch: (v) => {
      const n = toNumber(v);
      return n === null ? null : { 'case.lugWidth': n, 'case.lugWidthMm': n };
    },
  },
  {
    key: 'lugToLug',
    label: 'Lug-to-lug (mm)',
    getExisting: (r) => dispStr(loose(r).case?.lugToLugMm) || dispStr(loose(r).case?.lugToLug),
    buildPatch: (v) => {
      const n = toNumber(v);
      return n === null ? null : { 'case.lugToLug': n, 'case.lugToLugMm': n };
    },
  },
  // `bezelMaterial` is the suggestion key; `bezel` is the storage key — it has
  // always held the bezel MATERIAL, and the RN app / brand portal / dedup
  // differ all read it, so the short name stays.
  strField('bezelMaterial', 'Bezel material', (r) => dispStr(loose(r).bezel), 'bezel'),
  {
    // Bezel colour(s) — multi-select (lookup_bezel_colors), persisted as an
    // ARRAY at top-level `bezelColors` (the crownType/crystal convention, not
    // the comma-joined one handsColor/indexColor use). A GMT-Master "Pepsi"
    // insert is ["red", "blue"]. formatMultiLookupValues reads a comma-joined
    // legacy scalar too, so a ref imported before this field was an array still
    // reports its value and is left alone by a fill-empty run.
    key: 'bezelColors',
    label: 'Bezel colors',
    getExisting: (r) => formatMultiLookupValues(loose(r).bezelColors),
    buildPatch: (v) => {
      const a = toArray(v);
      return a === null ? null : { bezelColors: a };
    },
  },
  {
    // Case body colour(s) — multi-select (lookup_case_colors), persisted as an
    // ARRAY at top-level `caseColors`, the same convention bezelColors uses
    // rather than the comma-joined one handsColor/indexColor use. A two-tone
    // steel-and-gold case is ["steel", "gold"]. formatMultiLookupValues reads a
    // comma-joined legacy scalar too, so a ref imported before this field was an
    // array still reports its value and is left alone by a fill-empty run.
    key: 'caseColors',
    label: 'Case colors',
    getExisting: (r) => formatMultiLookupValues(loose(r).caseColors),
    buildPatch: (v) => {
      const a = toArray(v);
      return a === null ? null : { caseColors: a };
    },
  },
  strField('dialMaterial', 'Dial material', (r) => dispStr(r.dialAndHands?.material), 'dialAndHands.material'),
  strField('dialFinish', 'Dial finish', (r) => dispStr(r.dialAndHands?.finish), 'dialAndHands.finish'),
  {
    // MULTI-select, comma-joined at dialAndHands.dialFeatures — the structural
    // and decorative counterpart to the surface finish above.
    key: 'dialFeatures',
    label: 'Dial features',
    getExisting: (r) => dispJoined(loose(r).dialAndHands?.dialFeatures),
    buildPatch: (v) => {
      const j = toJoined(v);
      return j === null ? null : { 'dialAndHands.dialFeatures': j };
    },
  },
  {
    // Marker TYPE — how the markers are made (lookup_dial_marker_type). Stored
    // at dialAndHands.indexes under its historical name, as an ARRAY (not the
    // comma-joined convention handsColor/indexColor use below): multi-select
    // since the marker-type split, and shared-types v1.51.0 types it string[].
    // formatDialMarkerValues reads the pre-migration scalar too, so a ref that
    // has not been through migrateDialIndexesNumerals still reports its
    // existing value and is left alone by a fill-empty run.
    key: 'dialIndexes',
    label: 'Marker type',
    getExisting: (r) => formatDialMarkerValues(loose(r).dialAndHands?.indexes),
    buildPatch: (v) => {
      const a = toArray(v);
      return a === null ? null : { 'dialAndHands.indexes': a };
    },
  },
  {
    // Marker STYLE — what the markers are (lookup_dial_numerals). Same shape.
    key: 'dialNumerals',
    label: 'Dial numerals',
    getExisting: (r) => formatDialMarkerValues(loose(r).dialAndHands?.numerals),
    buildPatch: (v) => {
      const a = toArray(v);
      return a === null ? null : { 'dialAndHands.numerals': a };
    },
  },
  strField('hands', 'Hands style', (r) => dispStr(r.dialAndHands?.hands), 'dialAndHands.hands'),
  {
    key: 'handsColor',
    label: 'Hands color',
    getExisting: (r) => dispJoined(loose(r).dialAndHands?.handsColor),
    buildPatch: (v) => {
      const j = toJoined(v);
      return j === null ? null : { 'dialAndHands.handsColor': j };
    },
  },
  {
    // Colour of the hour indices/markers. Multi-select backed by
    // lookup_index_colors; persists as a comma-joined string at
    // dialAndHands.indexColor (NOT an array — mirrors handsColor). The AI may
    // return an array or joined string; toJoined normalises both.
    key: 'indexColor',
    label: 'Index color',
    getExisting: (r) => dispJoined(loose(r).dialAndHands?.indexColor),
    buildPatch: (v) => {
      const j = toJoined(v);
      return j === null ? null : { 'dialAndHands.indexColor': j };
    },
  },
  {
    // Functional hand types present (hour/minute/seconds/gmt/…). Multi-select
    // backed by lookup_hand_types; persists as a comma-joined string at
    // dialAndHands.handTypes (NOT an array — mirrors handsColor/indexColor).
    key: 'handTypes',
    label: 'Hand types',
    getExisting: (r) => dispJoined(loose(r).dialAndHands?.handTypes),
    buildPatch: (v) => {
      const j = toJoined(v);
      return j === null ? null : { 'dialAndHands.handTypes': j };
    },
  },
  // Lume — four SINGLE-select slugs stored flat under dialAndHands, same shape
  // as the date block below. Deliberately NOT tied to the `hasLume` boolean
  // spec: bulk enrich is fill-empty, so a ref that already answers the boolean
  // still has all four of these empty and is exactly the ref this run is for.
  strField('lumePresence', 'Lume presence', (r) => dispStr(loose(r).dialAndHands?.lumePresence), 'dialAndHands.lumePresence'),
  strField('lumeMaterial', 'Lume material', (r) => dispStr(loose(r).dialAndHands?.lumeMaterial), 'dialAndHands.lumeMaterial'),
  strField('lumeDayColor', 'Lume day color', (r) => dispStr(loose(r).dialAndHands?.lumeDayColor), 'dialAndHands.lumeDayColor'),
  strField('lumeGlowColor', 'Lume glow color', (r) => dispStr(loose(r).dialAndHands?.lumeGlowColor), 'dialAndHands.lumeGlowColor'),
  // Date complication — four SINGLE-select slugs stored flat under
  // dialAndHands, so plain strField rather than the joined/array helpers the
  // multi-selects above need.
  strField('dateDisplay', 'Date display', (r) => dispStr(r.dialAndHands?.dateDisplay), 'dialAndHands.dateDisplay'),
  strField('dateWindowPosition', 'Date window position', (r) => dispStr(r.dialAndHands?.dateWindowPosition), 'dialAndHands.dateWindowPosition'),
  strField('dateWindowFrame', 'Date window frame', (r) => dispStr(r.dialAndHands?.dateWindowFrame), 'dialAndHands.dateWindowFrame'),
  strField('dateWheelColor', 'Date wheel color', (r) => dispStr(r.dialAndHands?.dateWheelColor), 'dialAndHands.dateWheelColor'),
  {
    // The colour(s) of the numerals printed ON the date disc — the counterpart
    // to dateWheelColor above, which is the disc itself. The only MULTI-select
    // of the five (a day-date prints the weekend in a second colour), so it
    // persists comma-joined at dialAndHands.dateWheelTextColor, mirroring
    // handsColor / indexColor rather than the array convention.
    //
    // Backed by lookup_text_colors, shared with the single-select electronics
    // `textColor`. Because that shared doc is flagged isMultiSelect: false, the
    // enrich prompt's rendered token still heads its list "single value" and
    // the model may answer with a bare slug — toJoined normalises array and
    // scalar alike, so either shape lands.
    key: 'dateWheelTextColor',
    label: 'Date wheel text color',
    getExisting: (r) => dispJoined(r.dialAndHands?.dateWheelTextColor),
    buildPatch: (v) => {
      const j = toJoined(v);
      return j === null ? null : { 'dialAndHands.dateWheelTextColor': j };
    },
  },
  boolField('hasLume', 'Has lume', (r) => dispBool(r.dialAndHands?.luminova), 'dialAndHands.luminova'),
  intField('subdials', 'Subdials', (r) => dispStr(loose(r).dialAndHands?.subdials), 'dialAndHands.subdials'),
  {
    // WHAT each sub-register does — the companion to the subdials COUNT above,
    // which stays a plain integer. Multi-select backed by lookup_dial_subdials;
    // persists as a comma-joined string at dialAndHands.subdialTypes (NOT an
    // array — mirrors handTypes / indexColor). The AI may return an array or a
    // joined string; toJoined normalises both.
    //
    // A separate spec from `subdials` on purpose, not a derived one: bulk
    // enrich is fill-empty per field, so a ref that already has the count is
    // still exactly the ref this column is for.
    key: 'subdialTypes',
    label: 'Subdial types',
    getExisting: (r) => dispJoined(loose(r).dialAndHands?.subdialTypes),
    buildPatch: (v) => {
      const j = toJoined(v);
      return j === null ? null : { 'dialAndHands.subdialTypes': j };
    },
  },
  // Chapter ring & dial furniture. Three single-select slugs and one multi.
  // Independent columns, not a gated group: unlike the lume block, a `none`
  // answer in any of them says nothing about the other three, so each is filled
  // on its own merits.
  strField('chapterRing', 'Chapter ring', (r) => dispStr(loose(r).dialAndHands?.chapterRing), 'dialAndHands.chapterRing'),
  strField('minuteTrack', 'Minute track', (r) => dispStr(loose(r).dialAndHands?.minuteTrack), 'dialAndHands.minuteTrack'),
  {
    // The measurement scales the watch carries — dial OR bezel, one field for
    // both surfaces. Multi-select backed by lookup_dial_scales; persists
    // comma-joined at dialAndHands.dialScales (NOT an array — mirrors
    // subdialTypes above). The AI may return an array or a joined string;
    // toJoined normalises both.
    key: 'dialScales',
    label: 'Dial scales',
    getExisting: (r) => dispJoined(loose(r).dialAndHands?.dialScales),
    buildPatch: (v) => {
      const j = toJoined(v);
      return j === null ? null : { 'dialAndHands.dialScales': j };
    },
  },
  strField('logoType', 'Logo type', (r) => dispStr(loose(r).dialAndHands?.logoType), 'dialAndHands.logoType'),
  strField('strapColor', 'Strap color', (r) => dispStr(r.strap?.color), 'strap.color'),
  numField('strapWidth', 'Strap width (mm)', (r) => dispStr(loose(r).strap?.width), 'strap.width'),
  strField('strapBuckleType', 'Buckle type', (r) => dispStr(loose(r).strap?.buckleType), 'strap.buckleType'),
  boolField('isMicroAdjustable', 'Micro-adjustable', (r) => dispBool(loose(r).strap?.isMicroAdjustable), 'strap.isMicroAdjustable'),
  // Top-level, NOT under `strap` — a case-construction fact. Registered here as
  // well as in the prompt: a suggestion the registry does not list arrives from
  // the model and is dropped by the review UI without a word.
  boolField('hasIntegratedBracelet', 'Integrated bracelet', (r) => dispBool(loose(r).hasIntegratedBracelet), 'hasIntegratedBracelet'),
  intField('releaseYear', 'Release year', (r) => dispStr(r.production?.releaseYear), 'production.releaseYear'),
  // Asked for by the metadata-only profile (enrichReferenceLight); the full
  // prompt does not ask for them yet.
  intField('startYear', 'Production start', (r) => dispStr(r.production?.startYear), 'production.startYear'),
  intField('endYear', 'Production end', (r) => dispStr(r.production?.endYear), 'production.endYear'),
  boolField('discontinued', 'Discontinued', (r) => dispBool(r.knownDiscontinuation), 'knownDiscontinuation'),
  boolField('specialEdition', 'Special edition', (r) => dispBool(r.specialEdition), 'specialEdition'),
  boolField('isLimited', 'Limited edition', (r) => dispBool(r.production?.isLimited), 'production.isLimited'),
  intField('limitedEditionCount', 'Edition count', (r) => dispStr(r.production?.limitedSize), 'production.limitedSize'),
  strField('watchType', 'Watch type', (r) => dispStr(r.watchType), 'watchType'),
  {
    key: 'occasion',
    label: 'Occasion',
    getExisting: (r) => dispJoined(r.occasion),
    // handleSave persists occasion as a comma-joined string, not an array.
    buildPatch: (v) => {
      const j = toJoined(v);
      return j === null ? null : { occasion: j };
    },
  },
  {
    key: 'functions',
    label: 'Functions',
    getExisting: (r) => dispJoined(r.functions),
    buildPatch: (v) => {
      const a = toArray(v);
      return a === null ? null : { functions: a };
    },
  },
  strField('series', 'Series', (r) => dispStr(r.series), 'series'),
  strField('nickname', 'Nickname', (r) => dispStr(r.nickname), 'nickname'),
  strField('elecModule', 'Module', (r) => dispStr(r.electronics?.module), 'electronics.module'),
  strField('elecBattery', 'Battery', (r) => dispStr(r.electronics?.battery), 'electronics.battery'),
  strField('elecBatteryLife', 'Battery life', (r) => dispStr(r.electronics?.batteryLife), 'electronics.batteryLife'),
  strField('elecChargeTime', 'Charge time', (r) => dispStr(loose(r).electronics?.chargeTime), 'electronics.chargeTime'),
  strField('elecDisplay', 'Display type', (r) => dispStr(r.electronics?.display), 'electronics.display'),
  strField('elecLcdType', 'LCD type', (r) => dispStr(loose(r).electronics?.lcdType), 'electronics.lcdType'),
  strField('elecLightType', 'Light type', (r) => dispStr(loose(r).electronics?.lightType), 'electronics.lightType'),
  // The display's own colours — per-ref, never module-sourced, so unlike the
  // module-owned keys above a write here is not overwritten by the cache sync.
  strField('elecDisplayColor', 'Display color', (r) => dispStr(r.electronics?.displayColor), 'electronics.displayColor'),
  strField('elecTextColor', 'Text color', (r) => dispStr(r.electronics?.textColor), 'electronics.textColor'),
  strField('elecBacklightColor', 'Backlight color', (r) => dispStr(r.electronics?.backlightColor), 'electronics.backlightColor'),
  // ── Pocket-watch spec fields ────────────────────────────────────────────────
  // Nested under WatchReference.pocketWatch (populated when the watchType lookup
  // has showPocketWatch). The enrichReference callable returns these FLAT (grade,
  // caseStyle, …) and the single-ref editor routes them into the nested
  // pocketWatch.* object — the dotted-path patches below write the same canonical
  // location (updateDoc merges each nested field without clobbering siblings).
  // Lookup-backed slug fields are stored as-is (validation happens in the editor's
  // lookup picker; bulk accepts the AI slug like every other lookup field here).
  strField('grade', 'Grade (pocket watch)', (r) => dispStr(r.pocketWatch?.grade), 'pocketWatch.grade'),
  boolField('isKeyWound', 'Key-wound (pocket watch)', (r) => dispBool(r.pocketWatch?.isKeyWound), 'pocketWatch.isKeyWound'),
  strField('caseStyle', 'Case style (pocket watch)', (r) => dispStr(r.pocketWatch?.caseStyle), 'pocketWatch.caseStyle'),
  strField('escapement', 'Escapement (pocket watch)', (r) => dispStr(r.pocketWatch?.escapement), 'pocketWatch.escapement'),
  strField('jewelBearing', 'Jewel bearing (pocket watch)', (r) => dispStr(r.pocketWatch?.jewelBearing), 'pocketWatch.jewelBearing'),
  strField('movementSize', 'Movement size (pocket watch)', (r) => dispStr(r.pocketWatch?.movementSize), 'pocketWatch.movementSize'),
  {
    // Multi-select lookup (lookup_pocket_watch_adjustments). Unlike the dial
    // multi-selects (comma-joined strings), pocketWatch.adjustments persists as a
    // string[] (per PocketWatchInfo). Mirror `functions` — use toArray.
    key: 'adjustments',
    label: 'Adjustments (pocket watch)',
    getExisting: (r) => dispJoined(r.pocketWatch?.adjustments),
    buildPatch: (v) => {
      const a = toArray(v);
      return a === null ? null : { 'pocketWatch.adjustments': a };
    },
  },
  boolField('isFusee', 'Fusée (pocket watch)', (r) => dispBool(r.pocketWatch?.isFusee), 'pocketWatch.isFusee'),
];

export const ENRICH_FIELD_SPECS: Record<string, EnrichFieldSpec> = Object.fromEntries(
  SPEC_LIST.map((s) => [s.key, s]),
);

/** The suggestion keys that are true/false fields, derived from the registry. */
export const BOOLEAN_ENRICH_KEYS: ReadonlySet<string> = new Set(
  SPEC_LIST.filter((s) => s.isBoolean).map((s) => s.key),
);

/** Registry-defined field order — drives column ordering in the review grid. */
export const ENRICH_FIELD_ORDER: string[] = SPEC_LIST.map((s) => s.key);

export function getFieldSpec(key: string): EnrichFieldSpec | undefined {
  return ENRICH_FIELD_SPECS[key];
}

export function getFieldLabel(key: string): string {
  return ENRICH_FIELD_SPECS[key]?.label ?? key;
}

/** Does the suggested confidence meet the chosen write-back threshold? */
export function meetsThreshold(confidence: string | undefined, threshold: EnrichThreshold): boolean {
  const c = (confidence ?? 'medium').toLowerCase();
  return threshold === 'high' ? c === 'high' : c === 'high' || c === 'medium';
}

/**
 * Default checkbox state for a single (ref, field) cell, per the spec:
 *  - must meet the confidence threshold, AND
 *  - "fill empty only" → only when the ref has NO existing value (conflicts start
 *    unchecked); "overwrite" → checked regardless of existing value.
 */
/** How a proposed cell relates to what the document already holds. */
export type CellAgreement = 'new' | 'matches' | 'differs';

/**
 * Whether a proposal agrees with the stored value.
 *
 * The grids have always coloured a cell amber on `existing !== ''` alone, under
 * a variable called `conflict` — which means "this field already has a value",
 * NOT "the model disagrees". So an agreement was flagged identically to a
 * contradiction, and a reviewer scanning for real disagreements had to compare
 * every amber cell by eye.
 *
 * Compared as DISPLAY STRINGS, deliberately: both sides are already normalised
 * for rendering (arrays joined, booleans as Yes/No, numbers stringified), so
 * this asks the only question a reviewer can actually check — "do these two
 * read the same?" — rather than trying to compare a coerced value against a
 * stored one across five field types. Case and surrounding space are ignored
 * because neither changes the answer.
 */
export function cellAgreement(
  existingDisplay: string,
  proposedDisplay: string,
): CellAgreement {
  const existing = existingDisplay.trim();
  if (!existing) return 'new';
  return existing.toLowerCase() === proposedDisplay.trim().toLowerCase()
    ? 'matches'
    : 'differs';
}

/**
 * The three states a stored boolean can be in. `dispBool` renders them as '',
 * 'No' and 'Yes', and the grid only ever has the display string — so this is
 * how it recovers the value. UNSET is the one that matters: it is why a stored
 * `false` cannot simply be read as "the curator answered no".
 */
export type StoredBoolean = boolean | undefined;

/** Recover a stored boolean from the display string `dispBool` produced. */
export function storedBooleanFromDisplay(display: string): StoredBoolean {
  const d = display.trim().toLowerCase();
  if (d === '') return undefined;
  return d === 'yes' || d === 'true';
}

/**
 * Does a boolean suggestion pre-tick? (Confidence is judged separately — this
 * answers only the value question.)
 *
 *   stored unset  + true   → yes   an empty field, the case the tool is for
 *   stored unset  + false  → yes   an affirmative "no" is a finding too
 *   stored false  + true   → yes   the discovery: a default being flipped
 *   stored true   + false  → NO    de-flagging is a judgement call
 *   same as stored         → no    nothing to write
 *
 * The asymmetry is the point. A `false` on a ref usually means nobody has
 * looked, so raising it costs little and is easily undone; lowering a `true`
 * discards something someone (or an earlier run) asserted.
 */
/** How a boolean proposal relates to what is stored, in CellAgreement terms. */
export function booleanAgreement(stored: StoredBoolean, suggested: unknown): CellAgreement {
  const value = toBool(suggested);
  if (stored === undefined) return 'new';
  if (value === null) return 'differs';
  return stored === value ? 'matches' : 'differs';
}

export function booleanPreTicks(stored: StoredBoolean, suggested: unknown): boolean {
  const value = toBool(suggested);
  if (value === null) return false;
  if (stored === undefined) return true;
  if (stored === value) return false;
  return value === true;
}

export function defaultChecked(opts: {
  confidence: string | undefined;
  hasExisting: boolean;
  threshold: EnrichThreshold;
  writeMode: EnrichWriteMode;
}): boolean {
  if (!meetsThreshold(opts.confidence, opts.threshold)) return false;
  if (opts.writeMode === 'fill') return !opts.hasExisting;
  return true;
}

/**
 * Why a proposed cell starts checked — or, more usefully, why it does not.
 *
 * `defaultChecked` answers only "tick or don't", which is exactly the
 * information a reviewer is missing when a row they expected to be ticked isn't.
 * A medium-confidence proposal on an empty field, a low-confidence one, and a
 * calibre the catalogue has never heard of all render as the same blank
 * checkbox, so the tool looked like it had dropped the suggestion. Every
 * not-checked outcome is now a named reason the grid can put in a tooltip.
 *
 * `unresolvedCalibre` is not decidable here: it needs the live calibre corpus,
 * which only the component holds. The caller layers it on top.
 */
export type CheckedReason =
  | 'checked'
  | 'belowThreshold'
  | 'existingValue'
  | 'unresolvedCalibre'
  // The two the SINGLE-REF review table adds. Shared with the grid's vocabulary
  // rather than invented beside it, so a row means the same thing on both
  // screens; the grid never produces either (its `matches` case is covered by
  // `existingValue`, and it runs no lookup verification).
  | 'matchesCurrent'
  | 'unverifiedLookup'
  // Single-ref only: a regenerated description replacing machine-made or
  // legacy text (ticked), or a person's text (never ticked by default).
  | 'rewritesDescription'
  | 'manualDescription'
  // Set by the "Select:" shortcuts, so a row's tooltip still explains itself
  // after a blanket select or clear rather than describing the state it was in
  // when the dialog opened.
  | 'bulkCleared';

/** Reviewer-facing explanation for each reason, used as the cell's tooltip. */
export const CHECKED_REASON_LABEL: Record<CheckedReason, string> = {
  checked: 'Meets the confidence threshold — will be written on Apply.',
  belowThreshold:
    'Below the confidence threshold you chose, so it is not pre-selected. Tick it to write it anyway.',
  existingValue:
    'This field already has a value and the mode is “Fill empty fields only”. Switch to “Overwrite existing values”, or tick this cell, to write it.',
  unresolvedCalibre: 'No matching calibre in the catalogue — see the note on the cell.',
  matchesCurrent:
    'The suggestion is the value already stored, so there is nothing to write. Shown as confirmation.',
  unverifiedLookup:
    'Not in the current lookup list for this field, so it is not pre-selected. Review it before accepting.',
  rewritesDescription:
    'Replaces an AI-written, imported or untagged description. The first replaced text is kept in originalDescription.',
  manualDescription:
    'The stored description was written by a person, so it is never replaced by default. Tick it to replace it.',
  bulkCleared: 'Cleared by “Select: None”. Tick it to write it.',
};

/**
 * The default state of one (ref, field) cell AND the reason for it.
 *
 * Honours a spec's own `defaultChecked` override, so the reason never
 * contradicts the checkbox: description is pre-checked on high confidence even
 * over an existing value, and reports `checked` rather than `existingValue`.
 */
export function cellCheckState(
  spec: Pick<EnrichFieldSpec, 'defaultChecked' | 'isBoolean'>,
  opts: {
    confidence: string | undefined;
    hasExisting: boolean;
    threshold: EnrichThreshold;
    writeMode: EnrichWriteMode;
    /**
     * The stored value and the proposal, for a boolean field. Supplied by the
     * grid so a `false` can be told from an unanswered field — `hasExisting`
     * cannot, because 'No' is a non-empty display string.
     */
    booleanStored?: StoredBoolean;
    suggested?: unknown;
  },
): { checked: boolean; reason: CheckedReason } {
  if (spec.defaultChecked?.(opts)) return { checked: true, reason: 'checked' };
  if (!meetsThreshold(opts.confidence, opts.threshold)) {
    return { checked: false, reason: 'belowThreshold' };
  }
  // Booleans answer the value question their own way in FILL mode. Overwrite
  // mode is an explicit "write everything proposed", so it is left alone.
  if (spec.isBoolean && opts.writeMode === 'fill') {
    if (booleanPreTicks(opts.booleanStored, opts.suggested)) {
      return { checked: true, reason: 'checked' };
    }
    return opts.booleanStored !== undefined && toBool(opts.suggested) === opts.booleanStored
      ? { checked: false, reason: 'matchesCurrent' }
      : { checked: false, reason: 'existingValue' };
  }
  if (opts.writeMode === 'fill' && opts.hasExisting) {
    return { checked: false, reason: 'existingValue' };
  }
  return { checked: true, reason: 'checked' };
}

/**
 * Overlay the reviewer's manual ticks on top of the computed defaults.
 *
 * The defaults are DERIVED (results × threshold × write-mode) rather than
 * snapshotted into state at the end of a run, so anything that lands late — the
 * calibre corpus, most notably — corrects the grid instead of being frozen out
 * of it. This merge is what keeps a hand-toggled cell from being recomputed
 * away underneath the reviewer.
 */
export function mergeChecked(
  defaults: Record<string, Record<string, boolean>>,
  overrides: Record<string, Record<string, boolean>>,
): Record<string, Record<string, boolean>> {
  const out: Record<string, Record<string, boolean>> = {};
  for (const [refId, row] of Object.entries(defaults)) {
    out[refId] = overrides[refId] ? { ...row, ...overrides[refId] } : row;
  }
  // A manual tick on a ref with no computed defaults (every proposal skipped)
  // still counts — the reviewer asked for it explicitly.
  for (const [refId, row] of Object.entries(overrides)) {
    if (!out[refId]) out[refId] = { ...row };
  }
  return out;
}

/**
 * Build the `referenceData` payload for the enrichReference callable from a stored
 * ref, for the bulk "AI Enrich (full)" tool: the nested spec objects plus the
 * brand name and founded year and real catalogue siblings. What is never sent (pricing, media,
 * bookkeeping) and what the prompt does not need are decided in ONE place each —
 * `buildEnrichReferencePayload` and the callable's section allowlist.
 */
export function buildFullEnrichSnapshot(
  ref: WatchReference,
  brandName: string,
  siblingReferences: SiblingReference[],
  brandFounded?: number | null,
): Record<string, unknown> {
  return buildEnrichReferencePayload(ref as unknown as Record<string, unknown>, {
    brandName,
    brandFounded,
    siblingReferences,
  });
}

/**
 * Which enrichReference profile a bulk run uses: 'full' (enrichReference) or
 * 'metadata' (enrichReferenceLight — years and description only, no images).
 */
export type EnrichProfile = 'full' | 'metadata';

/**
 * The refs a run enriches under this profile. A metadata run only touches
 * parents with no release year: re-asking a dated ref buys nothing under
 * fill-empty, and variants get the parent's year by copy-down, not AI.
 * A full run takes the list as given.
 */
export function metadataCandidates(refs: WatchReference[], profile: EnrichProfile): WatchReference[] {
  if (profile !== 'metadata') return refs;
  return refs.filter((r) => {
    if (r.parentReferenceId?.trim()) return false;
    const year = r.production?.releaseYear as unknown;
    return year === undefined || year === null || year === '';
  });
}

/** Format a proposed suggestion value for compact display in a grid cell. */
export function formatSuggestion(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}
